// =============================================================================
// VehicleFormDialog.tsx - Fixed Dialog Component
// =============================================================================

"use client";

import { useState, useMemo, useEffect } from "react";
import { Search, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  VehicleType,
  Customer,
  VehicleMakeOutput,
  VehicleModelOutput,
} from "@/types";
import { toast } from "sonner";
import { makeService } from "@/services/vehicle/makeService";
import { modelService } from "@/services/vehicle/modelService";

export interface VehicleFormData {
  customerId: string;
  plateNumber: string;
  makeId: string;
  modelId: string;
  year: number;
  color: string;
  type: VehicleType;
  notes?: string;
}

interface VehicleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData: VehicleFormData;
  onFormDataChange: (data: VehicleFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  mode: "add" | "edit";
  customers: Customer[];
  makes: VehicleMakeOutput[];
  models: VehicleModelOutput[];
  onMakesRefresh: () => Promise<void>;
  onModelsRefresh: () => Promise<void>;
}

export function VehicleFormDialog({
  open,
  onOpenChange,
  formData,
  onFormDataChange,
  onSave,
  onCancel,
  mode,
  customers,
  makes,
  models,
  onMakesRefresh,
  onModelsRefresh,
}: VehicleFormDialogProps) {
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [makeSearchTerm, setMakeSearchTerm] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [isCreatingMake, setIsCreatingMake] = useState(false);
  const [isCreatingModel, setIsCreatingModel] = useState(false);
  const [makesKey, setMakesKey] = useState(0);
  const [modelsKey, setModelsKey] = useState(0);

  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm.trim()) {
      return customers;
    }
    const searchLower = customerSearchTerm.toLowerCase();
    return customers.filter(
      (customer) =>
        customer.name.toLowerCase().includes(searchLower) ||
        customer.phone.toLowerCase().includes(searchLower)
    );
  }, [customers, customerSearchTerm]);

  const filteredMakes = useMemo(() => {
    if (!makeSearchTerm.trim()) {
      return makes;
    }
    const searchLower = makeSearchTerm.toLowerCase();
    return makes.filter((make) =>
      make.name.toLowerCase().includes(searchLower)
    );
  }, [makes, makeSearchTerm]);

  const shouldShowAddMakeOption = useMemo(() => {
    if (!makeSearchTerm.trim()) {
      return false;
    }
    const searchLower = makeSearchTerm.toLowerCase().trim();
    return !makes.some((make) => make.name.toLowerCase() === searchLower);
  }, [makes, makeSearchTerm]);

  const filteredModels = useMemo(() => {
    if (!formData.makeId) {
      return [];
    }
    let modelsForMake = models.filter(
      (model) => model.makeId === formData.makeId
    );

    if (!modelSearchTerm.trim()) {
      return modelsForMake;
    }
    const searchLower = modelSearchTerm.toLowerCase();
    return modelsForMake.filter((model) =>
      model.name.toLowerCase().includes(searchLower)
    );
  }, [models, formData.makeId, modelSearchTerm]);

  const shouldShowAddModelOption = useMemo(() => {
    if (!formData.makeId || !modelSearchTerm.trim()) {
      return false;
    }
    const searchLower = modelSearchTerm.toLowerCase().trim();
    return !filteredModels.some(
      (model) => model.name.toLowerCase() === searchLower
    );
  }, [filteredModels, modelSearchTerm, formData.makeId]);

  const hasNoModelsForMake = useMemo(() => {
    if (!formData.makeId) {
      return false;
    }
    return (
      models.filter((model) => model.makeId === formData.makeId).length === 0
    );
  }, [models, formData.makeId]);

  const updateField = <K extends keyof VehicleFormData>(
    field: K,
    value: VehicleFormData[K]
  ) => {
    if (field === "makeId") {
      onFormDataChange({ ...formData, [field]: value, modelId: "" });
      setModelSearchTerm("");
    } else {
      onFormDataChange({ ...formData, [field]: value });
    }
  };

  const handleCreateMake = async () => {
    if (!makeSearchTerm.trim() || isCreatingMake) {
      return;
    }

    try {
      setIsCreatingMake(true);
      const newMake = await makeService.createMake({
        name: makeSearchTerm.trim(),
      });

      toast.success(`Make "${newMake.name}" created successfully`);

      // Refresh the makes list from parent
      await onMakesRefresh();

      // Force Select to re-render by updating the key
      setMakesKey((prev) => prev + 1);

      // Clear search and select the new make
      setMakeSearchTerm("");
      onFormDataChange({ ...formData, makeId: newMake.id, modelId: "" });
      setModelSearchTerm("");
    } catch (err: any) {
      console.error("Error creating make:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to create make. Please try again."
      );
    } finally {
      setIsCreatingMake(false);
    }
  };

  const handleCreateModel = async () => {
    if (!modelSearchTerm.trim() || !formData.makeId || isCreatingModel) {
      return;
    }

    try {
      setIsCreatingModel(true);
      const newModel = await modelService.createModel({
        name: modelSearchTerm.trim(),
        makeId: formData.makeId,
      });

      toast.success(`Model "${newModel.name}" created successfully`);

      // Refresh the models list from parent
      await onModelsRefresh();

      // Force Select to re-render by updating the key
      setModelsKey((prev) => prev + 1);

      // Clear search and select the new model
      setModelSearchTerm("");
      onFormDataChange({ ...formData, modelId: newModel.id });
    } catch (err: any) {
      console.error("Error creating model:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to create model. Please try again."
      );
    } finally {
      setIsCreatingModel(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === "add" ? "Add New Vehicle" : "Edit Vehicle"}
          </DialogTitle>
          <DialogDescription>
            {mode === "add"
              ? "Enter vehicle details. All fields are required."
              : "Update vehicle details."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer *</Label>
            <Select
              value={formData.customerId}
              onValueChange={(value) => {
                updateField("customerId", value);
                setCustomerSearchTerm("");
              }}
            >
              <SelectTrigger id="customer" className="w-full">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent className="overflow-y-auto max-h-[400px]">
                <div
                  className="sticky top-0 z-10 bg-popover border-b px-1 py-2"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or phone..."
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      className="pl-8 h-9"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} - {customer.phone}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                    No customers found
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="plateNumber">Plate Number *</Label>
            <Input
              id="plateNumber"
              value={formData.plateNumber}
              onChange={(e) => updateField("plateNumber", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="make">Make *</Label>
              <Select
                key={`make-select-${makesKey}-${makes.length}`}
                value={formData.makeId}
                onValueChange={(value) => {
                  updateField("makeId", value);
                  setMakeSearchTerm("");
                }}
              >
                <SelectTrigger id="make" className="w-full">
                  <SelectValue placeholder="Select make" />
                </SelectTrigger>
                <SelectContent className="overflow-y-auto max-h-[300px]" key={`make-content-${makesKey}-${makes.length}`}>
                  <div
                    className="sticky top-0 z-10 bg-popover border-b px-1 py-2"
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search make..."
                        value={makeSearchTerm}
                        onChange={(e) => setMakeSearchTerm(e.target.value)}
                        className="pl-8 h-9"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  {filteredMakes.length > 0 ? (
                    filteredMakes.map((make) => (
                      <SelectItem key={make.id} value={make.id}>
                        {make.name}
                      </SelectItem>
                    ))
                  ) : (
                    !shouldShowAddMakeOption && (
                      <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                        No makes found
                      </div>
                    )
                  )}
                  {shouldShowAddMakeOption && (
                    <div
                      className="px-2 py-2 border-t cursor-pointer hover:bg-accent"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        handleCreateMake();
                      }}
                    >
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <span>+</span>
                        <span>
                          Add "{makeSearchTerm.trim()}" as new make
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="px-2 py-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start h-auto py-2"
                      disabled={isCreatingMake}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (makeSearchTerm.trim()) {
                          handleCreateMake();
                        } else {
                          toast.info("Please enter a make name in the search field first");
                        }
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      <span className="text-sm">Create New Make</span>
                    </Button>
                  </div>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="model">Model *</Label>
              <Select
                key={`model-select-${modelsKey}-${models.length}-${formData.makeId}`}
                value={formData.modelId}
                onValueChange={(value) => {
                  updateField("modelId", value);
                  setModelSearchTerm("");
                }}
                disabled={!formData.makeId}
              >
                <SelectTrigger id="model" className="w-full">
                  <SelectValue
                    placeholder={
                      !formData.makeId
                        ? "Select make first"
                        : hasNoModelsForMake
                        ? "Enter new model"
                        : "Select model"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="overflow-y-auto max-h-[300px]" key={`model-content-${modelsKey}-${models.length}-${formData.makeId}`}>
                  <div
                    className="sticky top-0 z-10 bg-popover border-b px-1 py-2"
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder={
                          hasNoModelsForMake
                            ? "Enter new model"
                            : "Search model..."
                        }
                        value={modelSearchTerm}
                        onChange={(e) => setModelSearchTerm(e.target.value)}
                        className="pl-8 h-9"
                        disabled={!formData.makeId}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  {filteredModels.length > 0 ? (
                    filteredModels.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.name}
                      </SelectItem>
                    ))
                  ) : (
                    !shouldShowAddModelOption && (
                      <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                        {formData.makeId
                          ? "No models found"
                          : "Select a make first"}
                      </div>
                    )
                  )}
                  {shouldShowAddModelOption && (
                    <div
                      className="px-2 py-2 border-t cursor-pointer hover:bg-accent"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        handleCreateModel();
                      }}
                    >
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <span>+</span>
                        <span>
                          Add "{modelSearchTerm.trim()}" as new model
                        </span>
                      </div>
                    </div>
                  )}
                  {formData.makeId && (
                    <div className="px-2 py-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start h-auto py-2"
                        disabled={isCreatingModel || !formData.makeId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (modelSearchTerm.trim()) {
                            handleCreateModel();
                          } else {
                            toast.info("Please enter a model name in the search field first");
                          }
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        <span className="text-sm">Create New Model</span>
                      </Button>
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="year">Year *</Label>
              <Input
                id="year"
                type="number"
                value={formData.year}
                onChange={(e) =>
                  updateField(
                    "year",
                    parseInt(e.target.value) || new Date().getFullYear()
                  )
                }
              />
            </div>
            <div className="grid gap-2 w-full">
              <Label htmlFor="color">Color *</Label>
              <Select
                value={formData.color}
                onValueChange={(value) => updateField("color", value)}
              >
                <SelectTrigger id="color" className="w-full">
                  <SelectValue placeholder="Select color" />
                </SelectTrigger>
                <SelectContent className="overflow-y-auto max-h-[200px]">
                  <SelectItem value="white">White</SelectItem>
                  <SelectItem value="black">Black</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gray">Gray</SelectItem>
                  <SelectItem value="red">Red</SelectItem>
                  <SelectItem value="blue">Blue</SelectItem>
                  <SelectItem value="green">Green</SelectItem>
                  <SelectItem value="brown">Brown</SelectItem>
                  <SelectItem value="beige">Beige</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="orange">Orange</SelectItem>
                  <SelectItem value="yellow">Yellow</SelectItem>
                  <SelectItem value="purple">Purple</SelectItem>
                  <SelectItem value="burgundy">Burgundy</SelectItem>
                  <SelectItem value="maroon">Maroon</SelectItem>
                  <SelectItem value="navy">Navy</SelectItem>
                  <SelectItem value="teal">Teal</SelectItem>
                  <SelectItem value="pearl">Pearl</SelectItem>
                  <SelectItem value="champagne">Champagne</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2 w-full">
              <Label htmlFor="type">Vehicle Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value: VehicleType) =>
                  updateField("type", value)
                }
              >
                <SelectTrigger id="type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedan">Sedan</SelectItem>
                  <SelectItem value="suv">SUV</SelectItem>
                  <SelectItem value="truck">Truck</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes || ""}
              onChange={(e) => updateField("notes", e.target.value)}
              placeholder="Optional notes about the vehicle..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onSave}>
            {mode === "add" ? "Add Vehicle" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// PARENT COMPONENT IMPLEMENTATION EXAMPLE
// =============================================================================

/*
// In your parent component (e.g., VehiclesPage.tsx or similar):

import { useState, useEffect } from "react";
import { VehicleFormDialog } from "./VehicleFormDialog";
import { makeService } from "@/services/vehicle/makeService";
import { modelService } from "@/services/vehicle/modelService";

export function VehiclesPage() {
  const [makes, setMakes] = useState<VehicleMakeOutput[]>([]);
  const [models, setModels] = useState<VehicleModelOutput[]>([]);
  
  // Function to fetch makes from API
  const fetchMakes = async () => {
    try {
      const response = await makeService.getAllMakes();
      setMakes(response);
    } catch (error) {
      console.error("Error fetching makes:", error);
    }
  };
  
  // Function to fetch models from API
  const fetchModels = async () => {
    try {
      const response = await modelService.getAllModels();
      setModels(response);
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchMakes();
    fetchModels();
  }, []);
  
  return (
    <VehicleFormDialog
      // ... other props
      makes={makes}
      models={models}
      onMakesRefresh={fetchMakes}  // Pass the refresh function
      onModelsRefresh={fetchModels}  // Pass the refresh function
    />
  );
}
*/